import type { ReactElement } from 'react';
import { Loader2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface SkillStateCellProps {
  /** Server-computed row state; the cell never holds optimistic state. */
  marked: boolean;
  /** The skill's display name, so each switch gets a distinct accessible name. */
  name: string;
  /** Allowed -> instant, no confirmation. */
  onAllow: () => void;
  /** Marking only REQUESTS the dialog; it does not itself flip state. */
  onRequestMark: () => void;
  /** True from the click until the table has refetched. */
  pending: boolean;
  /** A support user can read the tab but cannot write rules. */
  disabled?: boolean;
  /** Why the switch is disabled. No message means no tooltip. */
  readOnlyMessage?: string;
}

/**
 * Allowed / Marked-for-delete switch for a skill row.
 *
 * Deliberately not the shared `BlockToggle`: its labels are the hardcoded
 * strings "Allowed"/"Blocked", and this list's verb is delete, not block
 * (KIR-4699 DOD). Adding a `labels` prop to a shared component to serve one
 * caller costs more than these few lines.
 *
 * Controlled from `marked`, so a cancelled dialog leaves the switch exactly
 * as it was.
 */
export function SkillStateCell({
  marked,
  name,
  onAllow,
  onRequestMark,
  pending,
  disabled = false,
  readOnlyMessage,
}: SkillStateCellProps): ReactElement {
  const control = (
    <Switch
      checked={!marked}
      onCheckedChange={checked => (checked ? onAllow() : onRequestMark())}
      disabled={disabled || pending}
      // A Radix switch is a <button>, and browsers force buttons to
      // `cursor: default` — so without this the switch showed an arrow
      // while the "Allowed" label beside it showed the row's pointer.
      className="cursor-pointer disabled:cursor-not-allowed data-[state=checked]:bg-green-600"
      // Names the CONTROL, not the next action. The old label flipped
      // between "Allow X" and "Mark for delete X", so a screen reader
      // announced the switch as the thing it would become rather than the
      // thing it is — and its checked state already carries that (KIR-4850
      // review).
      aria-label={`List state for ${name}`}
    />
  );

  return (
    // The row is click-to-expand, so without this the switch would both
    // change the rule and toggle the expansion.
    <div
      className="flex items-center gap-2"
      onClick={event => event.stopPropagation()}
    >
      {disabled && readOnlyMessage ? (
        <Tooltip>
          {/* A disabled Radix switch fires no pointer events and is skipped
              in tab order, so the trigger has to be a focusable wrapper or
              the tooltip never opens from the keyboard. */}
          <TooltipTrigger asChild>
            <span tabIndex={0} className="inline-flex">
              {control}
            </span>
          </TooltipTrigger>
          <TooltipContent>{readOnlyMessage}</TooltipContent>
        </Tooltip>
      ) : (
        control
      )}
      <span
        className={cn(
          'text-xs font-medium whitespace-nowrap',
          marked ? 'text-muted-foreground' : 'text-green-600'
        )}
      >
        {marked ? 'Marked for delete' : 'Allowed'}
      </span>
      {pending && (
        <Loader2
          aria-hidden
          className="size-3.5 animate-spin text-muted-foreground"
        />
      )}
    </div>
  );
}

SkillStateCell.displayName = 'SkillStateCell';
